import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Card, Row, Col, Badge, Spinner, Tab, Tabs, Table } from 'react-bootstrap';
import { FaMoneyBillWave, FaFingerprint, FaHistory, FaIdCard, FaShieldAlt, FaCheckCircle, FaUniversity, FaTimes, FaPlusCircle } from 'react-icons/fa';
import api from '../services/api';
import toast from 'react-hot-toast';

const AEPSCashDepositModal = ({ show, onHide, onSuccess }) => {
  const [activeTab, setActiveTab] = useState('cash_deposit');
  const [customerDetails, setCustomerDetails] = useState({
    aadharNumber: '',
    mobileNumber: '',
    accountNumber: '',
    bankCode: ''
  });
  const [depositDetails, setDepositDetails] = useState({
    amount: '',
    denomination: {},
    totalAmount: 0,
    charges: 0
  });
  const [fingerprintStatus, setFingerprintStatus] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [transactionHistory, setTransactionHistory] = useState([]);
  const [receiptData, setReceiptData] = useState(null);
  const [pidData, setPidData] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState(null);  // Actual DeviceInfo XML from RD Service
  const [location, setLocation] = useState({ latitude: '', longitude: '' });  // Store GPS location

  // AEPS Cash Deposit supported banks
  const depositBanks = [
    { code: 'SBIN', name: 'State Bank of India', charges: 25, dailyLimit: 50000 },
    { code: 'HDFC', name: 'HDFC Bank', charges: 20, dailyLimit: 100000 },
    { code: 'ICICI', name: 'ICICI Bank', charges: 20, dailyLimit: 100000 },
    { code: 'AXIS', name: 'Axis Bank', charges: 18, dailyLimit: 75000 },
    { code: 'PNB', name: 'Punjab National Bank', charges: 25, dailyLimit: 50000 },
    { code: 'BOB', name: 'Bank of Baroda', charges: 25, dailyLimit: 50000 },
    { code: 'CANARA', name: 'Canara Bank', charges: 22, dailyLimit: 75000 },
    { code: 'UNION', name: 'Union Bank of India', charges: 22, dailyLimit: 75000 },
    { code: 'IOB', name: 'Indian Overseas Bank', charges: 18, dailyLimit: 60000 },
    { code: 'CENTRAL', name: 'Central Bank of India', charges: 18, dailyLimit: 60000 }
  ];

  // Currency denominations
  const denominations = [
    { value: 2000, label: '₹2000', color: 'danger' },
    { value: 500, label: '₹500', color: 'warning' },
    { value: 200, label: '₹200', color: 'info' },
    { value: 100, label: '₹100', color: 'success' },
    { value: 50, label: '₹50', color: 'primary' },
    { value: 20, label: '₹20', color: 'secondary' },
    { value: 10, label: '₹10', color: 'dark' }
  ];

  const mockTransactionHistory = [
    {
      id: 'AECD001',
      aadhar: '****8765',
      account: '****5678',
      bank: 'HDFC Bank',
      amount: 15000,
      charges: 20,
      status: 'Success',
      date: '2025-01-15',
      time: '14:30:25'
    },
    {
      id: 'AECD002',
      aadhar: '****8765',
      account: '****1234',
      bank: 'State Bank of India',
      amount: 8000,
      charges: 25,
      status: 'Success',
      date: '2025-01-15',
      time: '10:15:42'
    },
    {
      id: 'AECD003',
      aadhar: '****9876',
      account: '****9876',
      bank: 'ICICI Bank',
      amount: 12500,
      charges: 20,
      status: 'Success',
      date: '2025-01-14',
      time: '16:45:12'
    }
  ];

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setMessage({ type: '', text: '' });
    setFingerprintStatus('pending');
    setReceiptData(null);
    setPidData(null);
    setDeviceInfo(null);
    setLocation({ latitude: '', longitude: '' });  // Reset location
    
    if (tab === 'history') {
      setTransactionHistory(mockTransactionHistory);
    }
  };

  const validateAadhar = (aadhar) => {
    // Allow 12 digits (real Aadhar) or 16 digits (UAT testing)
    return /^(?:\d{12}|\d{16})$/.test(aadhar);
  };

  const validateMobile = (mobile) => {
    return /^[6-9]\d{9}$/.test(mobile);
  };

  const validateAccount = (account) => {
    return /^\d{9,18}$/.test(account);
  };

  const updateDenomination = (value, count) => {
    const newDenomination = {
      ...depositDetails.denomination,
      [value]: Math.max(0, count)
    };
    
    const totalAmount = Object.entries(newDenomination).reduce((sum, [denom, cnt]) => {
      return sum + (parseInt(denom) * parseInt(cnt || 0));
    }, 0);

    const bank = depositBanks.find(b => b.code === customerDetails.bankCode);
    const charges = bank ? bank.charges : 0;

    setDepositDetails({
      ...depositDetails,
      denomination: newDenomination,
      totalAmount: totalAmount,
      charges: charges
    });
  };

  const captureFingerprint = async () => {
    if (!validateAadhar(customerDetails.aadharNumber)) {
      setMessage({ type: 'error', text: 'Please enter a valid 12-digit or 16-digit Aadhar number' });
      return;
    }

    if (!validateAccount(customerDetails.accountNumber)) {
      setMessage({ type: 'error', text: 'Please enter a valid account number' });
      return;
    }

    if (!customerDetails.bankCode) {
      setMessage({ type: 'error', text: 'Please select a bank' });
      return;
    }

    if (depositDetails.totalAmount < 100) {
      setMessage({ type: 'error', text: 'Minimum deposit amount is ₹100' });
      return;
    }

    if (depositDetails.totalAmount > 10000) {
      setMessage({ type: 'error', text: 'Maximum deposit amount is ₹10,000' });
      return;
    }

    const bank = depositBanks.find(b => b.code === customerDetails.bankCode);
    if (bank && depositDetails.totalAmount > bank.dailyLimit) {
      setMessage({ type: 'error', text: `Daily deposit limit for ${bank.name} is ₹${bank.dailyLimit.toLocaleString()}` });
      return;
    }

    setLoading(true);
    setFingerprintStatus('capturing');
    setMessage({ type: 'info', text: 'Please place your finger on the biometric device...' });

    try {
      const { captureFingerprint: rdCapture, getDeviceInfo, getGpsLocation } = await import('../utils/rdService');

      const gps = await getGpsLocation();
      setLocation(gps);

      // IDEMIA L1 RD Service - get DeviceInfo (required) + capture
      let deviceInfoXml;
      try {
        deviceInfoXml = await getDeviceInfo();
      } catch (e) {
        setFingerprintStatus('failed');
        setMessage({ type: 'error', text: `Device info failed: ${e.message}. Ensure RD Service is running.` });
        console.error('getDeviceInfo failed:', e);
        return;
      }
      const result = await rdCapture();

      if (result.success) {
        const pidDataXml = result.pidData;
        const qualityScore = result.qualityScore;
        if (qualityScore == null || qualityScore >= 50) {
          setPidData(pidDataXml);
          setDeviceInfo(deviceInfoXml);
          setFingerprintStatus('captured');
          setMessage({ type: 'success', text: 'Fingerprint captured successfully.' });
        } else {
          setFingerprintStatus('failed');
          setMessage({ type: 'error', text: `Fingerprint quality too low (${qualityScore}%). Please try again with a clean finger.` });
        }
      } else {
        setFingerprintStatus('failed');
        setMessage({ type: 'error', text: `Fingerprint capture failed (Error ${result.errorCode}: ${result.errorInfo}). Please try again.` });
      }
    } catch (error) {
      setFingerprintStatus('failed');
      const errorMessage = error.message || 'Fingerprint capture failed. Ensure IDEMIA L1 RD Service is running (port 11100).';
      setMessage({ type: 'error', text: errorMessage });
      console.error('Fingerprint capture error:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate unique reference number
  const generateReferenceNo = () => {
    return 'NSDL' + Date.now() + Math.random().toString(36).substr(2, 9).toUpperCase();
  };

  // Get bank IIN (Issuer Identification Number) for PaySprint NSDL Cash Deposit
  // PaySprint requires actual bank IIN, not arbitrary integers
  const getBankIIN = (bankCode) => {
    // Map bank codes to actual IINs (6-digit bank identification numbers)
    // These are standard IINs used by PaySprint/NSDL for AEPS transactions
    const bankIINMap = {
      'SBIN': 608001,    // State Bank of India
      'HDFC': 608002,    // HDFC Bank
      'ICICI': 608003,   // ICICI Bank
      'AXIS': 608004,    // Axis Bank
      'PNB': 608005,     // Punjab National Bank
      'BOB': 608006,     // Bank of Baroda
      'CANARA': 608007,  // Canara Bank
      'UNION': 608008,   // Union Bank of India
      'IOB': 608009,     // Indian Overseas Bank
      'CENTRAL': 608010  // Central Bank of India
    };
    return bankIINMap[bankCode] || 608001; // Default to SBI IIN if not found
  };

  const processDeposit = async () => {
    if (fingerprintStatus !== 'captured') {
      setMessage({ type: 'error', text: 'Please capture fingerprint first' });
      return;
    }

    if (!pidData) {
      setMessage({ type: 'error', text: 'Fingerprint data not available. Please capture fingerprint again.' });
      return;
    }

    if (!customerDetails.mobileNumber || customerDetails.mobileNumber.length !== 10) {
      setMessage({ type: 'error', text: 'Please enter a valid 10-digit mobile number' });
      return;
    }

    setLoading(true);
    setMessage({ type: 'info', text: 'Processing NSDL Cash Deposit...' });

    try {
      const bank = depositBanks.find(b => b.code === customerDetails.bankCode);
      const referenceNo = generateReferenceNo();

      // Format timestamp as Y-m-d H:i:s (required by PaySprint)
      const now = new Date();
      const timestamp = now.toISOString().slice(0, 19).replace('T', ' '); // Format: 2025-11-18 16:30:42

      // Build request payload for NSDL Cash Deposit V3 (PaySprint)
      const payload = {
        mobilenumber: customerDetails.mobileNumber,
        accessmodetype: 'APP',
        adhaarnumber: customerDetails.aadharNumber,
        latitude: location.latitude || '28.6139',
        longitude: location.longitude || '77.2090',
        referenceno: referenceNo,
        nationalbankidentification: getBankIIN(customerDetails.bankCode),
        data: pidData,
        device_info: deviceInfo || '',
        timestamp: timestamp,
        transactiontype: 'CD',
        submerchantid: 'PS002103',
        amount: depositDetails.totalAmount,
        is_iris: 'No',
      };

      // Call NSDL Cash Deposit V3 API
      const response = await api.post('/api/aeps/nsdl/cash-deposit', payload);

      // Check response - SUCCESS: txnstatus = 1 and status = true
      const isSuccess = response.data.success && 
                        response.data.txnstatus === 1 && 
                        response.data.data?.status === true;

      if (isSuccess) {
        const transactionData = {
          transactionId: response.data.data?.ackno || referenceNo,
          referenceNo: referenceNo,
          aadharNumber: customerDetails.aadharNumber,
          accountNumber: customerDetails.accountNumber,
          bank: bank?.name || customerDetails.bankCode,
          amount: depositDetails.totalAmount,
          charges: depositDetails.charges,
          status: 'Success',
          timestamp: new Date().toLocaleString(),
          txnstatus: response.data.txnstatus,
          responseCode: response.data.response_code
        };

        // Generate receipt
        setReceiptData({
          transactionId: transactionData.transactionId,
          referenceNo: referenceNo,
          aadharNumber: '****' + customerDetails.aadharNumber.slice(-4),
          accountNumber: '****' + customerDetails.accountNumber.slice(-4),
          bank: bank?.name || customerDetails.bankCode,
          amount: depositDetails.totalAmount,
          charges: depositDetails.charges,
          denomination: depositDetails.denomination,
          timestamp: new Date().toLocaleString(),
          location: 'NSDL Cash Deposit - Agent Location',
          ackno: response.data.data?.ackno || '',
          bankrrn: response.data.data?.bankrrn || ''
        });

        setMessage({ 
          type: 'success',
          text: response.data.message || response.data.data?.message || 'NSDL Cash deposit successful!'
        });

        toast.success('NSDL Cash deposit completed successfully!');

        if (onSuccess) {
          onSuccess(transactionData);
        }
      } else {
        // FAILED: txnstatus = 2 and status = true, or other error
        const errorMessage = response.data.message || 
                            response.data.data?.message || 
                            (response.data.txnstatus === 2 ? 'Transaction failed' : 'Deposit failed. Please try again.');
        
        setMessage({
          type: 'error',
          text: errorMessage
        });
        toast.error(errorMessage);
      }

    } catch (error) {
      console.error('NSDL Cash Deposit Error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || error.response?.data?.data?.message || 'Deposit failed. Please try again.'
      });
      toast.error('NSDL Cash deposit failed');
    } finally {
      setLoading(false);
    }
  };

  const continueProcessing = () => {
    // Reset for next transaction
    setActiveTab('cash_deposit');
    setReceiptData(null);
    setFingerprintStatus('pending');
    setDepositDetails({
      amount: '',
      denomination: {},
      totalAmount: 0,
      charges: 0
    });
    setMessage({ type: '', text: '' });
  };

  const getStatusBadge = (status) => {
    const variants = {
      'Success': 'success',
      'Failed': 'danger',
      'Pending': 'warning'
    };
    return <Badge bg={variants[status]}>{status}</Badge>;
  };

  const getFingerprintIcon = () => {
    switch (fingerprintStatus) {
      case 'captured':
        return <FaCheckCircle className="text-success" size={50} />;
      case 'failed':
        return <FaTimes className="text-danger" size={50} />;
      default:
        return <FaFingerprint className="text-primary" size={50} />;
    }
  };

  const handleReset = () => {
    setCustomerDetails({ aadharNumber: '', mobileNumber: '', accountNumber: '', bankCode: '' });
    setDepositDetails({ amount: '', denomination: {}, totalAmount: 0, charges: 0 });
    setFingerprintStatus('pending');
    setReceiptData(null);
    setMessage({ type: '', text: '' });
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton className="bg-warning text-dark">
        <Modal.Title>
          <FaPlusCircle className="me-2" />
          NSDL Cash Deposit Service (PaySprint)
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* AEPS Cash Deposit Tabs */}
        <Tabs activeKey={activeTab} onSelect={handleTabChange} className="mb-4">
          <Tab eventKey="cash_deposit" title={
            <span>
              <FaMoneyBillWave className="me-2" />
              Cash Deposit
            </span>
          }>
            <div className="p-2">
              <h6 className="text-warning mb-3">NSDL Cash Deposit (PaySprint V3)</h6>
            </div>
          </Tab>
          <Tab eventKey="history" title={
            <span>
              <FaHistory className="me-2" />
              Deposit History
            </span>
          }>
            <div className="p-2">
              <h6 className="text-warning mb-3">Cash Deposit History</h6>
            </div>
          </Tab>
        </Tabs>

        {/* Transaction History */}
        {activeTab === 'history' && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h6>Recent AEPS Cash Deposits</h6>
              <Badge bg="warning">Total: ₹35,500</Badge>
            </div>
            
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>Aadhar</th>
                  <th>Account</th>
                  <th>Bank</th>
                  <th>Amount</th>
                  <th>Charges</th>
                  <th>Status</th>
                  <th>Date & Time</th>
                </tr>
              </thead>
              <tbody>
                {transactionHistory.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>{transaction.id}</td>
                    <td>{transaction.aadhar}</td>
                    <td>{transaction.account}</td>
                    <td>{transaction.bank}</td>
                    <td>₹{transaction.amount.toLocaleString()}</td>
                    <td>₹{transaction.charges}</td>
                    <td>{getStatusBadge(transaction.status)}</td>
                    <td>
                      <small>{transaction.date}<br/>{transaction.time}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {/* Cash Deposit Form */}
        {activeTab === 'cash_deposit' && (
          <Row>
            {/* Customer Details & Denomination Counter */}
            <Col md={6}>
              {/* Customer Details */}
              <Card className="mb-4">
                <Card.Header>
                  <FaIdCard className="me-2" />
                  Customer Details
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Aadhar Number *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="12-digit Aadhar (or 16-digit for UAT)"
                      value={customerDetails.aadharNumber}
                      onChange={(e) => setCustomerDetails({
                        ...customerDetails, 
                        aadharNumber: e.target.value.replace(/\D/g, '').slice(0, 16)
                      })}
                    />
                    <Form.Text className="text-muted">
                      Enter 12-digit Aadhar number (or 16-digit for UAT testing)
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Account Number *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Bank account number"
                      value={customerDetails.accountNumber}
                      onChange={(e) => setCustomerDetails({
                        ...customerDetails, 
                        accountNumber: e.target.value.replace(/\D/g, '').slice(0, 18)
                      })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Mobile Number *</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="10-digit registered mobile number"
                      value={customerDetails.mobileNumber}
                      onChange={(e) => setCustomerDetails({
                        ...customerDetails, 
                        mobileNumber: e.target.value.replace(/\D/g, '').slice(0, 10)
                      })}
                    />
                    <Form.Text className="text-muted">
                      Required for NSDL Cash Deposit
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Select Bank *</Form.Label>
                    <Form.Select
                      value={customerDetails.bankCode}
                      onChange={(e) => {
                        const bankCode = e.target.value;
                        const bank = depositBanks.find(b => b.code === bankCode);
                        setCustomerDetails({...customerDetails, bankCode});
                        setDepositDetails(prev => ({
                          ...prev, 
                          charges: bank ? bank.charges : 0
                        }));
                      }}
                    >
                      <option value="">Choose bank...</option>
                      {depositBanks.map((bank) => (
                        <option key={bank.code} value={bank.code}>
                          {bank.name} (Charges: ₹{bank.charges})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  {customerDetails.bankCode && (
                    <Alert variant="info">
                      <strong>Daily Limit:</strong> ₹{depositBanks.find(b => b.code === customerDetails.bankCode)?.dailyLimit.toLocaleString()}<br/>
                      <strong>Service Charges:</strong> ₹{depositDetails.charges}<br/>
                      <strong>NSDL Cash Deposit:</strong> Amount range ₹100 - ₹10,000
                    </Alert>
                  )}
                </Card.Body>
              </Card>

              {/* Cash Denomination Counter */}
              <Card>
                <Card.Header>
                  <FaMoneyBillWave className="me-2" />
                  Cash Denomination Counter
                </Card.Header>
                <Card.Body>
                  {denominations.map((denom) => (
                    <Row key={denom.value} className="mb-3 align-items-center">
                      <Col md={4}>
                        <Badge bg={denom.color} className="fs-6 p-2">
                          {denom.label}
                        </Badge>
                      </Col>
                      <Col md={4}>
                        <div className="d-flex align-items-center">
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => updateDenomination(denom.value, (depositDetails.denomination[denom.value] || 0) - 1)}
                          >
                            -
                          </Button>
                          <Form.Control
                            type="number"
                            className="mx-2 text-center"
                            style={{ width: '70px' }}
                            value={depositDetails.denomination[denom.value] || 0}
                            onChange={(e) => updateDenomination(denom.value, parseInt(e.target.value) || 0)}
                            min="0"
                          />
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={() => updateDenomination(denom.value, (depositDetails.denomination[denom.value] || 0) + 1)}
                          >
                            +
                          </Button>
                        </div>
                      </Col>
                      <Col md={4}>
                        <strong>₹{((depositDetails.denomination[denom.value] || 0) * denom.value).toLocaleString()}</strong>
                      </Col>
                    </Row>
                  ))}
                  
                  <hr/>
                  <Row>
                    <Col md={8}>
                      <h5>Total Amount:</h5>
                    </Col>
                    <Col md={4}>
                      <h5 className="text-success">₹{depositDetails.totalAmount.toLocaleString()}</h5>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            {/* Biometric Authentication & Receipt */}
            <Col md={6}>
              {/* Biometric Authentication */}
              <Card className="mb-4">
                <Card.Header>
                  <FaFingerprint className="me-2" />
                  Biometric Authentication
                </Card.Header>
                <Card.Body className="text-center">
                  <div className="mb-4">
                    {getFingerprintIcon()}
                  </div>
                  
                  <div className="mb-3">
                    <Badge bg={
                      fingerprintStatus === 'captured' ? 'success' :
                      fingerprintStatus === 'failed' ? 'danger' :
                      fingerprintStatus === 'capturing' ? 'warning' : 'secondary'
                    }>
                      {fingerprintStatus === 'captured' ? 'Captured' :
                       fingerprintStatus === 'failed' ? 'Failed' :
                       fingerprintStatus === 'capturing' ? 'Capturing' : 'Pending'}
                    </Badge>
                  </div>

                  {fingerprintStatus === 'pending' && (
                    <Button 
                      variant="warning" 
                      onClick={captureFingerprint}
                      disabled={loading || depositDetails.totalAmount < 100}
                      className="mb-3"
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Capturing...
                        </>
                      ) : (
                        <>
                          <FaFingerprint className="me-2" />
                          Capture Fingerprint
                        </>
                      )}
                    </Button>
                  )}

                  {fingerprintStatus === 'captured' && (
                    <Button 
                      variant="success" 
                      onClick={processDeposit}
                      disabled={loading}
                      className="mb-3"
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Processing Deposit...
                        </>
                      ) : (
                        <>
                          <FaPlusCircle className="me-2" />
                          Process Deposit
                        </>
                      )}
                    </Button>
                  )}

                  <div className="mt-3">
                    <small className="text-muted">
                      Biometric authentication secured by UIDAI
                    </small>
                  </div>

                  {depositDetails.totalAmount > 0 && fingerprintStatus === 'pending' && (
                    <Alert variant="info" className="mt-3">
                      <strong>NSDL Cash Deposit Summary:</strong><br/>
                      Amount: ₹{depositDetails.totalAmount.toLocaleString()}<br/>
                      Charges: ₹{depositDetails.charges}<br/>
                      <strong>Net Deposit: ₹{(depositDetails.totalAmount - depositDetails.charges).toLocaleString()}</strong><br/>
                      <small className="text-muted">Amount must be between ₹100 - ₹10,000</small>
                    </Alert>
                  )}
                </Card.Body>
              </Card>

              {/* Deposit Receipt */}
              {receiptData && (
                <Card>
                  <Card.Header className="bg-success text-white">
                    <FaCheckCircle className="me-2" />
                    Deposit Receipt
                  </Card.Header>
                  <Card.Body>
                    <div className="text-center mb-3">
                      <h6>NSDL CASH DEPOSIT RECEIPT</h6>
                      <small>{receiptData.location}</small>
                    </div>
                    <hr/>
                    <div className="mb-1"><strong>Transaction ID:</strong> {receiptData.transactionId}</div>
                    {receiptData.referenceNo && (
                      <div className="mb-1"><strong>Reference No:</strong> {receiptData.referenceNo}</div>
                    )}
                    {receiptData.ackno && (
                      <div className="mb-1"><strong>Acknowledgment No:</strong> {receiptData.ackno}</div>
                    )}
                    {receiptData.bankrrn && (
                      <div className="mb-1"><strong>Bank RRN:</strong> {receiptData.bankrrn}</div>
                    )}
                    <div className="mb-1"><strong>Aadhar:</strong> {receiptData.aadharNumber}</div>
                    <div className="mb-1"><strong>Account:</strong> {receiptData.accountNumber}</div>
                    <div className="mb-1"><strong>Bank:</strong> {receiptData.bank}</div>
                    <div className="mb-1"><strong>Deposit Amount:</strong> ₹{receiptData.amount.toLocaleString()}</div>
                    <div className="mb-1"><strong>Service Charges:</strong> ₹{receiptData.charges}</div>
                    <hr/>
                    <div className="mb-3"><strong>Net Credit:</strong> ₹{(receiptData.amount - receiptData.charges).toLocaleString()}</div>
                    
                    <div className="mb-2"><strong>Denomination Breakdown:</strong></div>
                    {Object.entries(receiptData.denomination).map(([denom, count]) => (
                      count > 0 && (
                        <div key={denom} className="mb-1">
                          <small>₹{denom} × {count} = ₹{(parseInt(denom) * count).toLocaleString()}</small>
                        </div>
                      )
                    ))}
                    
                    <hr/>
                    <div className="mb-1"><strong>Date & Time:</strong> {receiptData.timestamp}</div>
                    <div className="text-center mt-3">
                      <small className="text-muted">Thank you for using NSDL Cash Deposit</small>
                    </div>
                  </Card.Body>
                </Card>
              )}
            </Col>
          </Row>
        )}

        {/* Message */}
        {message.text && (
          <Alert variant={
            message.type === 'error' ? 'danger' : 
            message.type === 'info' ? 'info' : 'success'
          } className="mt-3">
            {message.text}
          </Alert>
        )}

        {/* Security Notice */}
        <Alert variant="warning" className="mb-0">
          <FaShieldAlt className="me-2" />
          <strong>Secure NSDL Cash Deposit:</strong> All transactions are processed through PaySprint NSDL Cash Deposit V3 API with AES encryption and UIDAI-verified biometric authentication. Amount range: ₹100 - ₹10,000.
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            {activeTab === 'cash_deposit' && (
              <Button variant="outline-secondary" onClick={handleReset}>
                Reset
              </Button>
            )}
          </div>
          <div>
            <Button variant="secondary" onClick={onHide}>
              Close
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AEPSCashDepositModal;
